<template>
  <main v-if="article" class="learn-page">
    <nav class="article-nav" :aria-label="labels.articleNavigation">
      <router-link :to="guideIndexPath" class="back-link">
        <span class="material-icons">arrow_back</span>
        {{ labels.backToGuides }}
      </router-link>
      <router-link v-if="translation" :to="translation.path" class="language-link">
        <span class="material-icons" aria-hidden="true">translate</span>
        {{ labels.readTranslation }}
      </router-link>
    </nav>

    <article class="learn-article">
      <p class="learn-kicker">{{ labels.kicker }}</p>
      <h1>{{ article.title }}</h1>
      <p class="learn-description">{{ article.description }}</p>
      <div class="article-meta" :aria-label="labels.articleInformation">
        <span>{{ labels.by }} {{ article.author.name }}</span>
        <span>
          {{ labels.published }}
          <time :datetime="article.publishedAt">{{ formatDate(article.publishedAt) }}</time>
        </span>
        <span>
          {{ labels.updated }}
          <time :datetime="article.updatedAt">{{ formatDate(article.updatedAt) }}</time>
        </span>
      </div>

      <section class="tldr-box" aria-label="Quick answer">
        <strong>{{ labels.tldr }}</strong>
        <p>{{ article.tldr }}</p>
      </section>

      <section
        v-for="section in article.sections"
        :key="section.heading"
        class="learn-section"
      >
        <h2>{{ section.heading }}</h2>
        <p v-for="paragraph in section.body" :key="paragraph">
          {{ paragraph }}
        </p>
        <ol v-if="section.list">
          <li v-for="item in section.list" :key="item">{{ item }}</li>
        </ol>
        <div v-if="section.table" class="table-wrap">
          <table>
            <thead>
              <tr>
                <th v-for="header in section.table.headers" :key="header">{{ header }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="row in section.table.rows" :key="row.join('-')">
                <td v-for="cell in row" :key="cell">{{ cell }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>

      <section class="learn-section evidence-section">
        <h2>{{ labels.evidence }}</h2>
        <p>{{ article.methodology }}</p>
        <ul v-if="article.sources.length" class="source-list">
          <li v-for="source in article.sources" :key="source.url">
            <a :href="source.url" target="_blank" rel="noopener noreferrer">
              {{ source.title }}
            </a>
            <span>{{ source.publisher }}</span>
            <p>{{ source.note }}</p>
          </li>
        </ul>
        <p v-else class="evidence-note">{{ labels.noExternalEvidence }}</p>
      </section>

      <section class="learn-section faq-section">
        <h2>{{ labels.faq }}</h2>
        <details v-for="faq in article.faqs" :key="faq[0]">
          <summary>{{ faq[0] }}</summary>
          <p>{{ faq[1] }}</p>
        </details>
      </section>

      <section v-if="article.materialLinks" class="learn-section related-section">
        <h2>{{ labels.practiceMaterials }}</h2>
        <div class="related-links">
          <router-link v-for="link in article.materialLinks" :key="link.path" :to="link.path">
            <span>{{ link.label }}</span>
            <span class="material-icons" aria-hidden="true">arrow_forward</span>
          </router-link>
        </div>
      </section>

      <section class="learn-section related-section">
        <h2>{{ labels.related }}</h2>
        <div class="related-links">
          <router-link
            v-for="relatedArticle in relatedArticles"
            :key="relatedArticle.slug"
            :to="`${guideIndexPath}/${relatedArticle.slug}`"
          >
            <span>{{ relatedArticle.title }}</span>
            <span class="material-icons" aria-hidden="true">arrow_forward</span>
          </router-link>
        </div>
      </section>

      <section class="learn-cta">
        <h2>{{ labels.ctaTitle }}</h2>
        <p>{{ labels.ctaText }}</p>
        <div class="learn-cta-actions">
          <router-link to="/" class="primary-action">{{ labels.open }}</router-link>
          <router-link to="/material" class="secondary-action">{{ labels.materials }}</router-link>
        </div>
      </section>
    </article>
  </main>

  <main v-else class="learn-page">
    <router-link to="/" class="back-link">
      <span class="material-icons">arrow_back</span>
      {{ labels.back }}
    </router-link>
    <article class="learn-article">
      <h1>{{ labels.notFoundTitle }}</h1>
      <p class="learn-description">{{ labels.notFoundText }}</p>
    </article>
  </main>
</template>

<script>
import { computed } from 'vue'
import { useRoute } from 'vue-router'
import {
  learnArticles,
  zhLearnArticles,
  getLearnArticleBySlug,
  getZhLearnArticleBySlug,
  getArticleTranslation
} from '@/assets/seo/learnArticles'

export default {
  name: 'LearnArticle',
  setup() {
    const route = useRoute()
    const isChinese = computed(() => route.name === 'ZhLearnArticle')
    const article = computed(() => (
      isChinese.value
        ? getZhLearnArticleBySlug(route.params.slug)
        : getLearnArticleBySlug(route.params.slug)
    ))
    const guideIndexPath = computed(() => (isChinese.value ? '/zh/learn' : '/learn'))
    const translation = computed(() => (
      getArticleTranslation(isChinese.value ? 'zh' : 'en', route.params.slug)
    ))
    const formatDate = (value) => new Intl.DateTimeFormat(
      isChinese.value ? 'zh-CN' : 'en',
      { year: 'numeric', month: 'long', day: 'numeric', timeZone: 'UTC' }
    ).format(new Date(`${value}T00:00:00Z`))
    const relatedArticles = computed(() => {
      const source = isChinese.value ? zhLearnArticles : learnArticles
      const currentIndex = source.findIndex(item => item.slug === route.params.slug)
      if (currentIndex < 0 || source.length < 2) return []

      return Array.from({ length: Math.min(3, source.length - 1) }, (_, offset) => (
        source[(currentIndex + offset + 1) % source.length]
      ))
    })
    const labels = computed(() => (
      isChinese.value
        ? {
            back: '返回 GoodScriber',
            backToGuides: '返回中文学习指南',
            articleNavigation: '文章导航',
            readTranslation: 'Read in English',
            kicker: 'GoodScriber 中文指南',
            articleInformation: '文章信息',
            by: '作者：',
            published: '发布：',
            updated: '更新：',
            tldr: '一句话结论',
            evidence: '依据与适用边界',
            noExternalEvidence: '本文只描述可复现的产品操作流程，不把该流程表述为已经由外部研究验证的学习效果。',
            faq: '常见问题',
            related: '继续学习',
            practiceMaterials: '用这些素材练',
            ctaTitle: '在 GoodScriber 里试试这个流程',
            ctaText: '用 GoodScriber 生成字幕、把文本转成语音，并用自己的学习材料逐句跟读练习。',
            open: '打开 GoodScriber',
            materials: '浏览听读素材',
            notFoundTitle: '没有找到这篇指南',
            notFoundText: '这个 GoodScriber 中文指南暂时不可用。'
          }
        : {
            back: 'Back to GoodScriber',
            backToGuides: 'Back to learning guides',
            articleNavigation: 'Article navigation',
            readTranslation: '阅读中文版',
            kicker: 'GoodScriber guide',
            articleInformation: 'Article information',
            by: 'By',
            published: 'Published',
            updated: 'Updated',
            tldr: 'TL;DR',
            evidence: 'Evidence and limitations',
            noExternalEvidence: 'This page describes a reproducible product workflow and does not present the workflow as an externally validated learning outcome.',
            faq: 'FAQ',
            related: 'Related guides',
            practiceMaterials: 'Practice materials',
            ctaTitle: 'Try the workflow in GoodScriber',
            ctaText: 'Use GoodScriber to generate subtitles, create speech from text, and practice sentence by sentence with your own learning material.',
            open: 'Open GoodScriber',
            materials: 'Browse materials',
            notFoundTitle: 'Guide not found',
            notFoundText: 'This GoodScriber guide is not available.'
          }
    ))

    return {
      article,
      formatDate,
      guideIndexPath,
      translation,
      relatedArticles,
      labels
    }
  }
}
</script>

<style scoped>
.learn-page {
  min-height: 100vh;
  padding: calc(var(--navbar-height) + 32px) 24px 88px;
  color: var(--color-text);
}

.learn-article {
  width: min(880px, 100%);
  margin: 0 auto;
}

.article-nav {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  width: min(880px, 100%);
  margin: 0 auto 28px;
}

.article-nav .back-link {
  margin: 0;
}

.back-link {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  width: fit-content;
  margin: 0 calc((100% - min(880px, 100%)) / 2) 28px;
  min-height: 38px;
  padding: 0 16px 0 12px;
  color: var(--color-text-muted);
  text-decoration: none;
  font-size: 0.9rem;
  font-weight: 650;
  background: var(--workspace-control-bg);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-pill);
  transition: all 0.2s ease;
}

.back-link .material-icons {
  font-size: 18px;
}

.back-link:hover {
  color: var(--color-brand-600);
  background: var(--color-brand-50);
  border-color: var(--color-brand-200);
}

.language-link {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  min-height: 38px;
  padding: 0 14px;
  color: var(--workspace-accent-dark);
  text-decoration: none;
  font-size: 0.9rem;
  font-weight: 700;
  background: var(--workspace-active-bg);
  border: 1px solid rgba(23, 107, 77, 0.2);
  border-radius: var(--radius-pill);
}

.language-link .material-icons {
  font-size: 18px;
}

.language-link:hover {
  border-color: rgba(23, 107, 77, 0.42);
}

.learn-kicker {
  margin: 0 0 12px;
  color: var(--workspace-accent-dark);
  font-size: 0.78rem;
  font-weight: 800;
  text-transform: uppercase;
}

.learn-article h1 {
  max-width: 780px;
  margin: 0;
  color: var(--color-text);
  font-size: clamp(2rem, 5vw, 3rem);
  font-weight: 840;
  line-height: 1.08;
}

.learn-description {
  max-width: 720px;
  margin: 18px 0 0;
  color: var(--color-text-muted);
  font-size: 1.08rem;
  line-height: 1.7;
}

.article-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 8px 18px;
  margin-top: 16px;
  color: var(--color-text-muted);
  font-size: 0.86rem;
}

.article-meta span + span {
  position: relative;
}

.article-meta span + span::before {
  position: absolute;
  left: -11px;
  color: var(--color-border-strong);
  content: '•';
}

.tldr-box,
.learn-cta {
  margin-top: 34px;
  padding: 22px;
  background: rgba(255, 255, 255, 0.74);
  border: 1px solid rgba(23, 107, 77, 0.14);
  border-radius: var(--radius-md);
  box-shadow: var(--shadow-sm);
}

.tldr-box strong {
  display: block;
  margin-bottom: 8px;
  color: var(--workspace-accent-dark);
}

.tldr-box p,
.learn-cta p {
  margin: 0;
  color: var(--color-text-muted);
  line-height: 1.75;
}

.learn-section {
  margin-top: 38px;
}

.learn-section h2,
.learn-cta h2 {
  margin: 0 0 12px;
  color: var(--color-text);
  font-size: 1.45rem;
  font-weight: 780;
}

.learn-section p,
.learn-section li {
  color: var(--color-text-muted);
  font-size: 1rem;
  line-height: 1.78;
}

.evidence-section {
  padding: 22px;
  background: rgba(236, 253, 245, 0.38);
  border: 1px solid rgba(23, 107, 77, 0.16);
  border-radius: var(--radius-md);
}

.source-list {
  display: grid;
  gap: 14px;
  margin: 18px 0 0;
  padding: 0;
  list-style: none;
}

.source-list li {
  margin: 0;
  padding-top: 14px;
  border-top: 1px solid rgba(23, 107, 77, 0.14);
}

.source-list a {
  color: var(--workspace-accent-dark);
  font-weight: 750;
}

.source-list span {
  display: block;
  margin-top: 3px;
  color: var(--color-text-muted);
  font-size: 0.86rem;
}

.source-list p,
.evidence-note {
  margin-bottom: 0;
}

.learn-section ol {
  margin: 0;
  padding-left: 22px;
}

.learn-section li {
  margin-bottom: 8px;
}

.table-wrap {
  overflow-x: auto;
  margin-top: 16px;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  background: rgba(255, 255, 255, 0.76);
}

table {
  width: 100%;
  border-collapse: collapse;
}

th,
td {
  padding: 13px 14px;
  text-align: left;
  border-bottom: 1px solid var(--color-border);
  vertical-align: top;
}

th {
  color: var(--color-text);
  font-size: 0.82rem;
  font-weight: 800;
  background: rgba(236, 253, 245, 0.6);
}

td {
  color: var(--color-text-muted);
  line-height: 1.55;
}

tbody tr:last-child td {
  border-bottom: 0;
}

.faq-section details {
  border-top: 1px solid var(--color-border);
}

.faq-section details:last-child {
  border-bottom: 1px solid var(--color-border);
}

.faq-section summary {
  cursor: pointer;
  padding: 16px 0;
  color: var(--color-text);
  font-weight: 750;
}

.related-links {
  display: grid;
  gap: 10px;
}

.related-links a {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  align-items: center;
  gap: 16px;
  min-height: 54px;
  padding: 12px 14px;
  color: var(--color-text);
  background: rgba(255, 255, 255, 0.62);
  border: 1px solid var(--color-border);
  border-radius: 8px;
  font-weight: 720;
  line-height: 1.45;
  text-decoration: none;
}

.related-links a:hover {
  color: var(--workspace-accent-dark);
  border-color: rgba(23, 107, 77, 0.28);
}

.related-links .material-icons {
  font-size: 18px;
}

.faq-section details p {
  margin: 0 0 16px;
}

.learn-cta-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-top: 18px;
}

.primary-action,
.secondary-action {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 42px;
  padding: 0 18px;
  border-radius: var(--radius-pill);
  font-weight: 760;
  text-decoration: none;
}

.primary-action {
  color: var(--color-text-inverse);
  background: #111827;
}

.secondary-action {
  color: var(--workspace-accent-dark);
  background: var(--workspace-active-bg);
  border: 1px solid rgba(23, 107, 77, 0.16);
}

@media (max-width: 768px) {
  .learn-page {
    padding: calc(var(--navbar-height) + 20px) 16px 64px;
  }

  .back-link {
    margin-left: 0;
    margin-right: 0;
  }

  .article-nav {
    align-items: stretch;
    flex-direction: column;
  }

  .article-nav .back-link,
  .language-link {
    justify-content: center;
    width: 100%;
  }

  .learn-article h1 {
    font-size: 1.8rem;
  }

  .learn-description {
    font-size: 1rem;
  }

  .tldr-box,
  .learn-cta {
    padding: 18px;
  }
}
</style>
